import {
  chatboxCharacterSchema,
  chatboxFontSchema,
  chatboxRadiusSchema,
  chatboxSurfaceSchema,
  type Settings,
} from "@go-novus/admin-api";
import {
  Icon,
  Panel,
  SegmentedControl,
  SelectField,
  TextField,
} from "@go-novus/ui";
import { CHATBOX_THEME_PRESETS } from "@go-novus/widget";

import * as m from "#/paraglide/messages";

import { usePreviewColor } from "../hooks/use-preview-color";
import type { PreviewControl } from "../hooks/use-preview-control";
import { normalizeHexColor, type DraftErrors } from "../lib/settings-draft";
import { PreviewLayout, type DraftView } from "./preview";

type Chatbox = Settings["chatbox"];

const PRESET_LABEL: Record<
  (typeof CHATBOX_THEME_PRESETS)[number]["name"],
  () => string
> = {
  Rose: m.settings_swatch_rose,
  Forest: m.settings_swatch_forest,
  Indigo: m.settings_swatch_indigo,
  Default: m.settings_swatch_default,
  Minimal: m.settings_swatch_minimal,
  Luxury: m.settings_swatch_luxury,
  Warm: m.settings_swatch_warm,
  Playful: m.settings_swatch_playful,
};

const FONT_LABEL: Record<Chatbox["font"], () => string> = {
  sans: m.settings_font_sans,
  serif: m.settings_font_serif,
  theme: m.settings_font_theme,
};

/** 契约只收这三档字号。 */
const FONT_SIZES: Chatbox["fontSize"][] = [13, 14, 16];

const FONT_SIZE_LABEL: Record<Chatbox["fontSize"], () => string> = {
  13: m.settings_font_size_small,
  14: m.settings_font_size_standard,
  16: m.settings_font_size_large,
};

const SURFACE_LABEL: Record<Chatbox["surface"], () => string> = {
  light: m.settings_surface_light,
  dark: m.settings_surface_dark,
};

const CHARACTER_STYLE_LABEL: Record<Chatbox["character"], () => string> = {
  solid: m.settings_character_solid,
  watercolor: m.settings_character_watercolor,
};

/** 通用圆角可覆盖主题的圆角基准。 */
const RADII = [0, 8, 16] as const;

/** 毛玻璃是个开关，但摆在一排分段控件中间，用分段控件比 Switch 齐整。 */
const GLASS_OPTIONS: { label: () => string; value: string }[] = [
  { label: m.settings_glass_off, value: "off" },
  { label: m.settings_glass_on, value: "on" },
];

const RADIUS_LABEL: Record<(typeof RADII)[number], () => string> = {
  0: m.settings_radius_square,
  8: m.settings_radius_rounded,
  16: m.settings_radius_round,
};

function readFontSize(value: string): Chatbox["fontSize"] | null {
  return FONT_SIZES.find((size) => String(size) === value) ?? null;
}

export interface AppearanceSectionProps {
  draftView: DraftView;
  errors: DraftErrors;
  onChange: (next: Chatbox) => void;
  preview: PreviewControl;
}

/** Appearance 标签：作用于整个 Chatbox 的主题色、背景、圆角、毛玻璃、角色画法和文字，右边一张预览。 */
export function AppearanceSection({
  draftView,
  errors,
  onChange,
  preview,
}: AppearanceSectionProps) {
  const { chatbox } = draftView;
  const pickerColor = usePreviewColor(chatbox.themeColor);
  const themeColor = normalizeHexColor(chatbox.themeColor);
  const preset = CHATBOX_THEME_PRESETS.find(
    (swatch) => swatch.accent === themeColor,
  );
  const presetRadius = preset?.radius ?? chatbox.radius;
  const radiusOptions = RADII.map((radius) => ({
    label: RADIUS_LABEL[radius](),
    value: String(radius),
  }));
  if (!RADII.some((radius) => radius === presetRadius)) {
    radiusOptions.push({
      label: m.settings_radius_preset(),
      value: String(presetRadius),
    });
  }
  // 自定义 Hex 或切换预设后仍可能保留过另一套主题的圆角。
  if (!radiusOptions.some(({ value }) => value === String(chatbox.radius))) {
    radiusOptions.push({
      label: `${String(chatbox.radius)}px`,
      value: String(chatbox.radius),
    });
  }

  function patch(next: Partial<Chatbox>): void {
    onChange({ ...chatbox, ...next });
  }

  /** 自定义颜色照收原样，合法时才推进预览；不重置已选的圆角。 */
  function patchThemeColor(value: string): void {
    patch({ themeColor: value });
  }

  return (
    <PreviewLayout draftView={draftView} preview={preview}>
      <Panel>
        <h2 className="px-4.5 pt-4.5 text-headline font-bold text-[var(--content-fg-strong)]">
          {m.settings_color_shape_heading()}
        </h2>
        <div className="p-4.5">
          <div className="text-label font-semibold">
            {m.settings_theme_color_label()}
          </div>
          <div
            aria-label={m.settings_theme_color_label()}
            className="mt-2 flex flex-wrap gap-2.5"
            role="group"
          >
            {CHATBOX_THEME_PRESETS.map((swatch) => (
              <button
                aria-label={PRESET_LABEL[swatch.name]()}
                aria-pressed={swatch.accent === themeColor}
                className="grid size-8.5 cursor-pointer place-items-center rounded-[7px] border-3 border-[var(--bg-surface-base)] p-0 text-[var(--content-fg-on-color)] outline-1 outline-[var(--content-fg-subtle)] aria-pressed:outline-2 aria-pressed:outline-[var(--content-fg-strong)] dark:border-[var(--bg-surface-subtle)]"
                key={swatch.accent}
                onClick={() => {
                  patch({ themeColor: swatch.accent, radius: swatch.radius });
                }}
                style={{ background: swatch.accent, color: swatch.onPrimary }}
                title={PRESET_LABEL[swatch.name]()}
                type="button"
              >
                {swatch.accent === themeColor && (
                  <Icon name="check" size={14} />
                )}
              </button>
            ))}
          </div>
          <div className="mt-1.5 text-micro text-[var(--content-fg-subtle)]">
            {preset ? PRESET_LABEL[preset.name]() : m.settings_swatch_custom()}
          </div>
          <div className="mt-3.5 grid grid-cols-[6rem_minmax(9.5rem,13.75rem)] gap-2.5">
            <div>
              <label
                className="mb-1.5 block text-micro font-bold text-[var(--content-fg-subtle)]"
                htmlFor="settings-theme-color-picker"
              >
                {m.settings_color_picker_label()}
              </label>
              {/* 组件库没有取色控件，这里用原生 input，样式走 token。 */}
              <input
                className="h-9 w-full cursor-pointer rounded-[7px] border border-[var(--border-surface-default)] bg-[var(--bg-surface-base)] p-0.75"
                id="settings-theme-color-picker"
                onChange={(event) => {
                  patchThemeColor(event.target.value);
                }}
                type="color"
                value={pickerColor.toLowerCase()}
              />
            </div>
            <TextField
              error={errors.themeColor}
              label={m.settings_hex_label()}
              onChange={(event) => {
                patchThemeColor(event.target.value);
              }}
              value={chatbox.themeColor}
            />
          </div>
          <div className="mt-1.75 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]">
            {m.settings_theme_color_hint()}
          </div>
          <div className="mt-4.5">
            <SegmentedControl
              hint={m.settings_surface_hint()}
              label={m.settings_surface_label()}
              onChange={(value) => {
                const surface = chatboxSurfaceSchema.safeParse(value);

                if (surface.success) {
                  patch({ surface: surface.data });
                }
              }}
              options={chatboxSurfaceSchema.options.map((value) => ({
                label: SURFACE_LABEL[value](),
                value,
              }))}
              value={chatbox.surface}
            />
          </div>
          <div className="mt-4.5">
            <SegmentedControl
              label={m.settings_radius_label()}
              onChange={(value) => {
                const radius = chatboxRadiusSchema.safeParse(Number(value));

                if (radius.success) {
                  patch({ radius: radius.data });
                }
              }}
              options={radiusOptions}
              value={String(chatbox.radius)}
            />
          </div>
          <div className="mt-4.5">
            <SegmentedControl
              hint={m.settings_glass_hint()}
              label={m.settings_glass_label()}
              onChange={(value) => {
                patch({ glass: value === "on" });
              }}
              options={GLASS_OPTIONS.map(({ label, value }) => ({
                label: label(),
                value,
              }))}
              value={chatbox.glass ? "on" : "off"}
            />
          </div>
          <div className="mt-4.5">
            <SegmentedControl
              hint={m.settings_character_hint()}
              label={m.settings_character_label()}
              onChange={(value) => {
                const character = chatboxCharacterSchema.safeParse(value);

                if (character.success) {
                  patch({ character: character.data });
                }
              }}
              options={chatboxCharacterSchema.options.map((value) => ({
                label: CHARACTER_STYLE_LABEL[value](),
                value,
              }))}
              value={chatbox.character}
            />
          </div>
        </div>
      </Panel>
      <Panel>
        <h2 className="px-4.5 pt-4.5 text-headline font-bold text-[var(--content-fg-strong)]">
          {m.settings_text_heading()}
        </h2>
        <div className="p-4.5">
          <div className="grid grid-cols-1 gap-2.5 @min-[30rem]:grid-cols-2">
            <SelectField
              label={m.settings_font_label()}
              onValueChange={(value) => {
                const font = chatboxFontSchema.safeParse(value);

                if (font.success) {
                  patch({ font: font.data });
                }
              }}
              options={chatboxFontSchema.options.map((value) => ({
                label: FONT_LABEL[value](),
                value,
              }))}
              value={chatbox.font}
            />
            <SelectField
              label={m.settings_font_size_label()}
              onValueChange={(value) => {
                const fontSize = readFontSize(value);

                if (fontSize !== null) {
                  patch({ fontSize });
                }
              }}
              options={FONT_SIZES.map((size) => ({
                label: FONT_SIZE_LABEL[size](),
                value: String(size),
              }))}
              value={String(chatbox.fontSize)}
            />
          </div>
          <div className="mt-1.75 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]">
            {m.settings_font_hint()}
          </div>
        </div>
      </Panel>
    </PreviewLayout>
  );
}
